import React, { useCallback, useEffect, useRef, useState } from "react";
import { CButton } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";

import { useRunDetail } from "@src/contexts/RunDetailContext";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import {
  CANCELABLE_ORDER_STATUSES,
  COMMAND_POLL_MAX_ATTEMPTS,
  EXECUTION_POLL_DELAY_MS,
} from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";

interface Props {
  executionId: number;
  positionId: number;
  orderId: number;
  symbol?: string;
}

/** The worker's result for a completed cancel-limit-order. Framework 0.3.38 does not send `outcome`. */
interface CancelOrderResult {
  outcome?: string;
}

const describeCancelResult = (
  orderId: number,
  positionId: number,
  result: CancelOrderResult | null | undefined,
): { type: "success" | "warning"; message: string } => {
  const order = `Order #${orderId} on position #${positionId}`;
  switch (result?.outcome) {
    case "terminal_fill":
      return { type: "warning", message: `${order} filled completely.` };
    case "terminal_cancel":
      return { type: "warning", message: `${order} ended with a fill.` };
    // The worker can return venue_zero_fill without raw proof of a zero fill, so it gets the neutral text.
    case "venue_zero_fill":
    default:
      return {
        type: "success",
        message: `Cancel request for order #${orderId} on position #${positionId} processed.`,
      };
  }
};

export const CancelLimitOrderButton: React.FC<Props> = ({
  executionId,
  positionId,
  orderId,
  symbol,
}) => {
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollAbortRef = useRef(false);

  // Cleanup on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
    };
  }, []);

  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { updateRun } = useRunDetail();

  // TODO: Extract this polling logic into a shared useCommandPolling hook.
  // This pattern is repeated in EndExecutionButtons.tsx and OrderCommandForm.tsx.
  // The hook should accept: executionId, callbacks (onComplete, onFail), options (maxAttempts, interval),
  // and return: { pollCommand, isPolling }.
  // Poll command status until terminal state
  const pollCommandStatus = useCallback(
    async (commandId: number) => {
      setPolling(true);
      pollAbortRef.current = false;
      let reachedTerminalState = false;

      for (let attempt = 0; attempt < COMMAND_POLL_MAX_ATTEMPTS; attempt++) {
        if (pollAbortRef.current) break;

        await delay(EXECUTION_POLL_DELAY_MS);

        try {
          const command = await tdApi.getExecutionCommand(executionId, commandId);
          if (!command) continue;

          if (command.status === "completed") {
            reachedTerminalState = true;
            console.log("[CancelLimitOrderButton] Command completed:", {
              commandId,
              executionId,
              positionId,
              orderId,
              result: command.result,
              command,
            });
            const { type, message } = describeCancelResult(
              orderId,
              positionId,
              command.result as CancelOrderResult | null | undefined,
            );
            showToast({
              type,
              title: `Command #${commandId} Executed`,
              message,
              timeout: 8000,
            });

            // Fetch full execution with positions and update Redux
            try {
              const execution = await tdApi.getExecution(executionId, {
                embed: "positions(movements(order))",
              });
              if (execution) {
                updateRun(execution);
                console.log(
                  "[CancelLimitOrderButton] Execution refreshed in Redux, positions:",
                  execution.positions?.length,
                );
              }
            } catch (err) {
              console.error("[CancelLimitOrderButton] Failed to refresh execution:", err);
            }
            break;
          } else if (command.status === "failed") {
            reachedTerminalState = true;
            const errorMsg = command.error_message || "Unknown error";
            const isAlreadyFilled =
              errorMsg.toLowerCase().includes("filled") ||
              errorMsg.toLowerCase().includes("not found") ||
              errorMsg.toLowerCase().includes("does not exist") ||
              errorMsg.toLowerCase().includes("already");

            console.log("[CancelLimitOrderButton] Command failed:", {
              commandId,
              executionId,
              positionId,
              orderId,
              error: errorMsg,
              isAlreadyFilled,
              command,
            });

            // Show appropriate message based on error type
            if (isAlreadyFilled) {
              showToast({
                type: "warning",
                title: "Order Already Processed",
                message: `Order #${orderId} was already filled or canceled. Refreshing data...`,
                timeout: 8000,
              });
            } else {
              showToast({
                type: "danger",
                title: `Command #${commandId} Failed`,
                message: errorMsg,
                timeout: 15000,
              });
            }

            // Always fetch fresh execution data on failure to update UI
            try {
              const execution = await tdApi.getExecution(executionId, {
                embed: "positions(movements(order))",
              });
              if (execution) {
                updateRun(execution);
                console.log(
                  "[CancelLimitOrderButton] Execution refreshed after failure, positions:",
                  execution.positions?.length,
                );
              }
            } catch (err) {
              console.error(
                "[CancelLimitOrderButton] Failed to refresh execution after failure:",
                err,
              );
            }
            break;
          }
          // Still processing, continue polling
        } catch (err) {
          console.error("[CancelLimitOrderButton] Polling error:", err);
        }
      }

      // Warn when the attempts run out before the command completes or fails.
      if (!reachedTerminalState && !pollAbortRef.current) {
        console.warn("[CancelLimitOrderButton] Polling timed out:", {
          commandId,
          executionId,
          positionId,
          orderId,
          maxAttempts: COMMAND_POLL_MAX_ATTEMPTS,
        });
        showToast({
          type: "warning",
          title: `Command #${commandId} Timeout`,
          message: `Could not confirm the cancel result for order #${orderId}.`,
          timeout: 15000,
        });
      }

      if (!pollAbortRef.current) {
        setPolling(false);
      }
    },
    [executionId, positionId, orderId, showToast, updateRun],
  );

  // Helper to find order status from execution data
  const findOrderStatus = (execution: T.ApiRun): string | null => {
    if (!execution.positions) return null;

    const position = execution.positions.find((p) => p.id === positionId);
    if (!position) return null;

    const movements = position.movements;
    const movement = movements?.find((m) => m.order?.id === orderId);
    return movement?.order?.order_status_name?.toLowerCase() || null;
  };

  const handleClick = async () => {
    setLoading(true);

    try {
      // First, fetch fresh execution data to check if order is still cancelable
      const freshExecution = await tdApi.getExecution(executionId, {
        embed: "positions(movements(order))",
      });

      if (!freshExecution) {
        throw new Error("Failed to fetch execution data");
      }

      // Update Redux with fresh data
      updateRun(freshExecution);

      // Check current order status
      const currentStatus = findOrderStatus(freshExecution);
      console.log("[CancelLimitOrderButton] Current order status:", {
        orderId,
        positionId,
        currentStatus,
      });

      if (
        !currentStatus ||
        !(CANCELABLE_ORDER_STATUSES as readonly string[]).includes(currentStatus)
      ) {
        // Order is no longer cancelable (already filled, canceled, etc.)
        showToast({
          type: "warning",
          title: "Order Already Processed",
          message: `Order #${orderId} status is "${currentStatus || "unknown"}". The view has been refreshed with the latest data.`,
          timeout: 8000,
        });
        setLoading(false);
        return;
      }

      // Order is still cancelable, show confirmation dialog
      const posInfo = symbol ? `#${positionId} (${symbol})` : `#${positionId}`;
      const confirmMsg = `Cancel pending limit order for position ${posInfo}?`;

      const { confirmed } = await confirmModal(confirmMsg, {
        title: "Cancel Limit Order",
        confirmLabel: "Cancel Order",
        cancelLabel: "Keep Order",
      });
      if (!confirmed) {
        setLoading(false);
        return;
      }

      // Send cancel command
      const command = await tdApi.createExecutionCommand(executionId, {
        command_type_name: "cancel-limit-order",
        payload: { order_id: orderId, position_id: positionId },
      });

      if (!command?.id) {
        throw new Error("Command created but no ID returned");
      }

      console.log("[CancelLimitOrderButton] Command sent:", {
        commandId: command.id,
        executionId,
        positionId,
        orderId,
        symbol,
        command,
      });
      showToast({
        type: "info",
        title: `Command #${command.id} Sent`,
        message: `Cancel request sent for order #${orderId}.`,
        timeout: 5000,
      });

      // Start polling for command status (runs in background)
      pollCommandStatus(command.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[CancelLimitOrderButton] Failed to send command:", {
        executionId,
        positionId,
        orderId,
        error: err,
      });
      showToast({
        type: "danger",
        title: "Command Failed",
        message: `Failed to send cancel command: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CButton
      color="warning"
      size="sm"
      variant="outline"
      onClick={handleClick}
      disabled={loading || polling}
      className="py-0 px-2"
    >
      {loading ? (
        <CSpinnerText text="Sending..." />
      ) : polling ? (
        <CSpinnerText text="Canceling..." />
      ) : (
        "Cancel Limit"
      )}
    </CButton>
  );
};
